import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import {
  PageHeader, SectionCard, DataTable, EmptyState, SearchBar, FilterBar,
  LoadingState, Pill, StatusBadge,
} from "@/components/shared";

const LIFECYCLE_TONES = { lead: "info", active_client: "success", inactive_client: "neutral" };
const initialsOf = (n) => (n || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

function RecentStrip({ onOpen }) {
  const [recent, setRecent] = useState([]);
  const [att, setAtt] = useState({});
  useEffect(() => {
    let list = [];
    try { list = JSON.parse(localStorage.getItem("tf.ws.recent") || "[]"); } catch { list = []; }
    if (list.length) {
      api.get(`/workspace/attention/batch?ids=${list.map((x) => x.id).join(",")}`)
        .then((r) => {
          const data = r.data || {};
          // prune "recently viewed" chips whose client no longer exists (deleted)
          const valid = list.filter((x) => Object.prototype.hasOwnProperty.call(data, x.id));
          if (valid.length !== list.length) {
            try { localStorage.setItem("tf.ws.recent", JSON.stringify(valid)); } catch { /* ignore */ }
          }
          setRecent(valid);
          setAtt(data);
        })
        .catch(() => setRecent(list));
    } else {
      setRecent(list);
    }
  }, []);
  if (!recent.length) return null;
  return (
    <div className="mb-5" data-testid="ws-recent-strip">
      <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        <Icons.History className="h-3.5 w-3.5" />Recently Viewed
      </div>
      <div className="flex flex-wrap gap-2.5">
        {recent.map((r) => {
          const a = att[r.id] || {};
          const overdue = a.overdue_tasks > 0, unread = a.unread_messages > 0;
          const needsAttn = overdue || unread;
          const dot = overdue ? "bg-rose-500" : "bg-blue-500";
          const tip = [unread ? `${a.unread_messages} unread message(s)` : null,
                       overdue ? `${a.overdue_tasks} overdue task(s)` : null].filter(Boolean).join(" · ");
          return (
            <button key={r.id} onClick={() => onOpen(r.id)} data-testid={`ws-recent-${r.id}`} title={tip || r.name}
              className="relative shrink-0 flex items-center gap-2.5 bg-white border border-slate-200 hover:border-blue-400 hover:shadow-sm rounded-xl pl-2.5 pr-3.5 py-2 transition-all duration-150">
              {needsAttn && (
                <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5" data-testid={`ws-recent-dot-${r.id}`}>
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${dot}`} />
                  <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${dot}`} />
                </span>
              )}
              <div className="h-8 w-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-[11px] font-semibold shrink-0">{initialsOf(r.name)}</div>
              <div className="text-left">
                <div className="text-sm font-medium text-slate-800 whitespace-nowrap leading-tight">{r.name}</div>
                <div className="text-[10px] text-slate-400 font-mono">{r.number || "—"}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ClientWorkspacePicker() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/workspace/clients", { params: query ? { q: query } : {} });
      setRows(data || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [query]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const columns = [
    { key: "client_number", label: "Client #" },
    { key: "full_name", label: "Name" },
    { key: "lifecycle_status", label: "Lifecycle" },
    { key: "country_of_residence", label: "Country", className: "hidden md:table-cell" },
    { key: "assigned_team_name", label: "Team", className: "hidden lg:table-cell" },
    { key: "last_activity_at", label: "Last Activity", className: "hidden lg:table-cell" },
  ];

  const renderCell = (r, c) => {
    switch (c.key) {
      case "client_number": return <span className="font-mono text-xs text-slate-500">{r.client_number}</span>;
      case "full_name": return (
        <div className="flex items-center gap-2.5">
          <div className="h-7 w-7 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-[11px] font-semibold">{(r.full_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}</div>
          <span className="font-medium text-slate-800">{r.full_name || "—"}</span>
        </div>);
      case "lifecycle_status": return <StatusBadge status={r.lifecycle_status} map={LIFECYCLE_TONES} />;
      case "assigned_team_name": return <span className="text-slate-500">{r.assigned_team_name || "—"}</span>;
      case "last_activity_at": return <span className="text-slate-500">{r.last_activity_at ? new Date(r.last_activity_at).toLocaleDateString() : "—"}</span>;
      default: return <span className="text-slate-500">{r[c.key] || "—"}</span>;
    }
  };

  return (
    <div data-testid="workspace-picker-page">
      <PageHeader title="Client Workspace" description="Select a client to open their 360° operational command center.">
        <FilterBar><SearchBar value={query} onChange={setQuery} placeholder="Search clients…" className="w-full sm:w-80" testId="ws-picker-search" /></FilterBar>
      </PageHeader>
      <RecentStrip onOpen={(id) => navigate(`/client-workspace/${id}`)} />
      <SectionCard>
        {loading ? <LoadingState label="Loading clients…" /> : (
          <DataTable columns={columns} rows={rows} renderCell={renderCell} testId="ws-picker-table"
            empty={<EmptyState icon="Users" title="No clients yet" description="Create contacts in the CRM first." />} />
        )}
      </SectionCard>
      <RowBinder onRowClick={(id) => navigate(`/client-workspace/${id}`)} />
    </div>
  );
}

function RowBinder({ onRowClick }) {
  useEffect(() => {
    const table = document.querySelector('[data-testid="ws-picker-table"]');
    if (!table) return;
    const handler = (e) => {
      const tr = e.target.closest('[data-testid^="row-"]');
      if (tr) onRowClick(tr.getAttribute("data-testid").replace("row-", ""));
    };
    table.addEventListener("click", handler);
    table.style.cursor = "pointer";
    return () => table.removeEventListener("click", handler);
  });
  return null;
}
